import React, { useState, useEffect } from 'react';
import { api } from '../../config/api.ts';
import { Plan, Course } from '../../types';
import { ModernCard } from '../ui/ModernCard.tsx';
import { ModernButton } from '../ui/ModernButton.tsx';
import { ChevronLeftIcon } from '../icons/index.tsx';
import { PDFPreviewModal } from '../ui/PDFPreviewModal.tsx';

interface PlanStepProps {
  selectedCourse: Course;
  selectedPlan: Plan | null;
  selectedDeliverables: string[];
  calculatedPrice: number;
  onSelectPlan: (plan: Plan) => void;
  onSelectDeliverables: (deliverables: string[]) => void;
  onCalculatedPrice: (price: number) => void;
  onNext: () => void;
  onBack: () => void;
  setError: (error: string) => void;
  setLoading: (loading: boolean) => void;
}

export const PlanStep: React.FC<PlanStepProps> = ({ 
  selectedCourse, 
  selectedPlan, 
  selectedDeliverables, 
  calculatedPrice,
  onSelectPlan, 
  onSelectDeliverables, 
  onCalculatedPrice,
  onNext, 
  onBack, 
  setError, 
  setLoading 
}) => {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [previewModal, setPreviewModal] = useState<{
    isOpen: boolean;
    pdfUrl: string;
    fileName: string;
    isConfirmationMode: boolean;
    pendingPlan: Plan | null;
  }>({
    isOpen: false,
    pdfUrl: '',
    fileName: '',
    isConfirmationMode: false,
    pendingPlan: null
  });

  useEffect(() => {
    if (selectedCourse) {
      loadPlans(selectedCourse.course_id);
    }
  }, [selectedCourse]);

  useEffect(() => {
    if (selectedPlan && selectedDeliverables.length > 0) {
      calculatePrice();
    }
  }, [selectedDeliverables, selectedPlan]);

  const loadPlans = async (courseId: number) => {
    try {
      setLoading(true);
      const response = await api.get(`/public/courses/${courseId}/plans`);
      if (response.data.success) {
        setPlans(response.data.data.plans);
      }
    } catch (err) {
      setError('فشل في تحميل الخطط');
    } finally {
      setLoading(false);
    }
  };
const isArabic = (text: string) => {
  const arabicPattern = /[\u0600-\u06FF]/;
  return arabicPattern.test(text);
};

const getDeliverableDisplay = (planId: number, deliverable: string): string => {
  if (planId === 123 && deliverable === 'PD2') {
    return 'PD2 - قد يختلف السعر حسب الاغراض المختارة'; 
  }
  
  return deliverable;
};
  const calculatePrice = async () => {
    if (!selectedPlan) return;
    try {
      const response = await api.post('/public/plans/calculate-price', {
        plan_id: selectedPlan.id,
        deliverables: selectedDeliverables
      });
      if (response.data.success) {
        onCalculatedPrice(response.data.data.price);
      }
    } catch (err) {
      setError('فشل في حساب السعر');
    }
  };

  const handlePlanSelect = (plan: Plan) => {
    // If plan has sample file, show it for confirmation
    if (plan.has_sample_file && plan.sample_file_url) {
      setPreviewModal({
        isOpen: true,
        pdfUrl: plan.sample_file_url,
        fileName: `نموذج ${plan.title}`,
        isConfirmationMode: true,
        pendingPlan: plan
      });
    } else {
      // No sample file, select plan directly
      confirmPlanSelection(plan);
    }
  };

  const confirmPlanSelection = (plan: Plan) => {
    onSelectPlan(plan);
    onSelectDeliverables([]);
    onCalculatedPrice(plan.student_price_kwd);
  };

  const handleConfirmPlan = () => {
    if (previewModal.pendingPlan) {
      confirmPlanSelection(previewModal.pendingPlan);
    }
    closeModal();
  };

  const handleRejectPlan = () => {
    closeModal();
  };

  const closeModal = () => {
    setPreviewModal({
      isOpen: false,
      pdfUrl: '',
      fileName: '',
      isConfirmationMode: false,
      pendingPlan: null
    });
  };

  const openPdfInNewTab = () => {
    if (previewModal.pdfUrl) {
      window.open(previewModal.pdfUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const toggleDeliverable = (deliverable: string) => {
    const newDeliverables = selectedDeliverables.includes(deliverable)
      ? selectedDeliverables.filter(d => d !== deliverable)
      : [...selectedDeliverables, deliverable];
    onSelectDeliverables(newDeliverables);
  };

  const handlePreviewSample = (plan: Plan) => {
    if (plan.sample_file_url) {
      setPreviewModal({
        isOpen: true,
        pdfUrl: plan.sample_file_url,
        fileName: `نموذج ${plan.title}`,
        isConfirmationMode: false,
        pendingPlan: null
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-gray-900">اختر الخطة</h2>
          <p className="text-gray-600">{selectedCourse?.display_name}</p>
        </div>
        <ModernButton 
          variant="outline" 
          size="sm"
          onClick={onBack}
          icon={<ChevronLeftIcon />}
        >
          رجوع
        </ModernButton>
      </div>
      
      <div className="space-y-4">
        {plans.map(plan => (
          <ModernCard key={plan.id} className="overflow-hidden">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-bold text-gray-900">{plan.title}</h3>
                  <div className="flex items-center space-x-2 space-x-reverse mt-1">
                    <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                      plan.type === 'project' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
                    }`}>
                      {plan.type === 'project' ? 'مشروع' : 'مهمة'}
                    </span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-green-600">
                    {selectedPlan?.id === plan.id ? calculatedPrice : plan.student_price_kwd} KWD
                  </div>
                </div>
              </div>
              
<p className={`text-gray-600 mb-4 leading-relaxed ${
  isArabic(plan.description) ? 'text-right' : 'text-left'
}`} 
  dir={isArabic(plan.description) ? 'rtl' : 'ltr'}>
  {plan.description}
</p>              
              {/* Sample File Button - Only show if plan is already selected */}
              {plan.has_sample_file && selectedPlan?.id === plan.id && (
                <div className="mb-4">
                  <ModernButton
                    variant="outline"
                    size="sm"
                    onClick={() => handlePreviewSample(plan)}
                    className="w-full sm:w-auto"
                  >
                    <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                    عرض نموذج الملف مرة أخرى
                  </ModernButton>
                </div>
              )}
              
              {plan.deliverables.length > 0 && selectedPlan?.id === plan.id && (
                <div className="mb-6 p-4 bg-gray-50 rounded-xl">
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    اختر المخرجات:
                  </label>
                  <div className="grid grid-cols-1 gap-2">
                    {plan.deliverables.map(deliverable => (
                      <label key={deliverable} className="flex items-center p-2 hover:bg-white rounded-lg transition-colors cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedDeliverables.includes(deliverable)}
                          onChange={() => toggleDeliverable(deliverable)}
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
<span className="mr-3 text-sm font-medium text-gray-700">
  {getDeliverableDisplay(plan.id, deliverable)}
</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="flex items-center justify-between">
                <ModernButton
                  variant={selectedPlan?.id === plan.id ? "primary" : "outline"}
                  onClick={() => handlePlanSelect(plan)}
                  size="sm"
                >
                  {selectedPlan?.id === plan.id ? "محدد" : "اختر الخطة"}
                </ModernButton>
                
                {selectedPlan?.id === plan.id && (
                  <ModernButton
                    variant="success"
                    onClick={onNext}
                    disabled={plan.deliverables.length > 0 && selectedDeliverables.length === 0}
                  >
                    استمرار ←
                  </ModernButton>
                )}
              </div>
            </div>
          </ModernCard>
        ))}
      </div>

      {/* Enhanced PDF Preview Modal with Confirmation */}
      <PDFPreviewModal
        isOpen={previewModal.isOpen}
        onClose={previewModal.isConfirmationMode ? handleRejectPlan : closeModal}
        pdfUrl={previewModal.pdfUrl}
        fileName={previewModal.fileName}
        customFooter={previewModal.isConfirmationMode ? (
          <div className="p-3 bg-gray-50 border-t border-gray-200">
            <div className="text-center mb-3">
              <h4 className="text-sm font-semibold text-gray-900 mb-1">
                هل يتطابق هذا النموذج مع مهمتك؟
              </h4>
              <button
                onClick={openPdfInNewTab}
                className="text-blue-600 hover:text-blue-800 text-xs underline"
              >
                فتح في نافذة جديدة
              </button>
            </div>
            <div className="flex space-x-2 space-x-reverse">
              <ModernButton
                variant="success"
                onClick={handleConfirmPlan}
                className="flex-1"
                size="sm"
              >
                نعم، أؤكد
              </ModernButton>
              <ModernButton
                variant="outline"
                onClick={handleRejectPlan}
                className="flex-1"
                size="sm"
              >
                لا، اختر غيرها
              </ModernButton>
            </div>
          </div>
        ) : undefined}
      />
    </div>
  );
};